import React, { useEffect, useState } from 'react';
import {
  FormattedDate,
  injectIntl,
  intlShape,
} from '@edx/frontend-platform/i18n';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Button, ProgressBar } from '@openedx/paragon';
import { getConfig } from '@edx/frontend-platform';

import { RequestStatus } from '../../data/constants';
import CourseStepper from '../../generic/course-stepper';
import { IMPORT_STAGES } from '../data/constants';
import { fetchImportStatus } from '../data/thunks';
import {
  getCurrentStage, getError, getFileName, getLoadingStatus, getProgress, getSavingStatus, getSuccessDate,
} from '../data/selectors';
import {
  getScormPreparationStatus,
  retryScormPreparation,
  startScormPreparation,
} from '../data/api';
import messages from './messages';

const POLLING_INTERVAL = 3000;
const ACTIVE_SCORM_PREPARATION_STATUSES = ['queued', 'running'];

const ImportStepper = ({ intl, courseId }) => {
  const currentStage = useSelector(getCurrentStage);
  const fileName = useSelector(getFileName);
  const { hasError, message: errorMessage } = useSelector(getError);
  const progress = useSelector(getProgress);
  const dispatch = useDispatch();
  const loadingStatus = useSelector(getLoadingStatus);
  const savingStatus = useSelector(getSavingStatus);
  const successDate = useSelector(getSuccessDate);
  const [scormPreparation, setScormPreparation] = useState(null);
  const [isScormPreparationLoading, setIsScormPreparationLoading] = useState(false);
  const [isScormPreparationUnavailable, setIsScormPreparationUnavailable] = useState(false);
  const isStopFetching = currentStage === IMPORT_STAGES.SUCCESS
    || loadingStatus === RequestStatus.FAILED
    || savingStatus === RequestStatus.FAILED
    || hasError;
  const formattedErrorMessage = hasError ? errorMessage || intl.formatMessage(messages.defaultErrorMessage) : '';

  useEffect(() => {
    const id = setInterval(() => {
      if (isStopFetching) {
        clearInterval(id);
      } else if (fileName) {
        dispatch(fetchImportStatus(courseId, fileName));
      }
    }, POLLING_INTERVAL);
    return () => clearInterval(id);
  }, [courseId, currentStage, dispatch, fileName, isStopFetching]);

  useEffect(() => {
    let isMounted = true;

    const loadScormPreparation = async () => {
      setIsScormPreparationLoading(true);
      setIsScormPreparationUnavailable(false);
      try {
        const status = successDate
          ? await getScormPreparationStatus(courseId, successDate)
          : await getScormPreparationStatus(courseId);
        if (!isMounted) {
          return;
        }
        setScormPreparation(status);
        if (status.status === 'not_started' && status.canStart) {
          const startedStatus = successDate
            ? await startScormPreparation(courseId, successDate)
            : await startScormPreparation(courseId);
          if (isMounted) {
            setScormPreparation(startedStatus);
          }
        }
      } catch {
        if (isMounted) {
          setIsScormPreparationUnavailable(true);
        }
      } finally {
        if (isMounted) {
          setIsScormPreparationLoading(false);
        }
      }
    };

    if (currentStage === IMPORT_STAGES.SUCCESS) {
      loadScormPreparation();
    } else {
      setScormPreparation(null);
      setIsScormPreparationUnavailable(false);
    }

    return () => {
      isMounted = false;
    };
  }, [courseId, currentStage, successDate]);

  useEffect(() => {
    if (!scormPreparation || !ACTIVE_SCORM_PREPARATION_STATUSES.includes(scormPreparation.status)) {
      return undefined;
    }

    let isMounted = true;
    const id = setInterval(async () => {
      try {
        const status = successDate
          ? await getScormPreparationStatus(courseId, successDate)
          : await getScormPreparationStatus(courseId);
        if (isMounted) {
          setScormPreparation(status);
        }
      } catch {
        if (isMounted) {
          setIsScormPreparationUnavailable(true);
        }
        clearInterval(id);
      }
    }, POLLING_INTERVAL);
    return () => {
      isMounted = false;
      clearInterval(id);
    };
  }, [courseId, scormPreparation, successDate]);

  let successTitle = intl.formatMessage(messages.stepperSuccessTitle);
  const localizedSuccessDate = successDate ? (
    <FormattedDate
      value={successDate}
      year="2-digit"
      month="2-digit"
      day="2-digit"
      hour="numeric"
      minute="numeric"
    />
  ) : null;
  if (localizedSuccessDate && currentStage === IMPORT_STAGES.SUCCESS) {
    const successWithDate = (
      <>
        {successTitle} ({localizedSuccessDate})
      </>
    );
    successTitle = successWithDate;
  }

  const handleRedirectCourseOutline = () => window.location.replace(`${getConfig().STUDIO_BASE_URL}/course/${courseId}`);
  const handleScormPreparation = async () => {
    setIsScormPreparationLoading(true);
    setIsScormPreparationUnavailable(false);
    try {
      const request = scormPreparation.canRetry
        ? retryScormPreparation
        : startScormPreparation;
      const status = successDate
        ? await request(courseId, successDate)
        : await request(courseId);
      setScormPreparation(status);
    } catch {
      setIsScormPreparationUnavailable(true);
    } finally {
      setIsScormPreparationLoading(false);
    }
  };

  const renderScormPreparation = () => {
    if (isScormPreparationUnavailable) {
      return <Alert className="mt-3" variant="warning">{intl.formatMessage(messages.scormPreparationUnavailable)}</Alert>;
    }
    if (isScormPreparationLoading && !scormPreparation) {
      return <div className="mt-3" role="status" aria-live="polite">{intl.formatMessage(messages.scormPreparationLoading)}</div>;
    }
    if (!scormPreparation) {
      return null;
    }

    const {
      status,
      totalModules = 0,
      processedModules = 0,
      moduleResults = [],
    } = scormPreparation;
    const statusMessage = {
      queued: messages.scormPreparationQueued,
      running: messages.scormPreparationRunning,
      complete: messages.scormPreparationCompleted,
      complete_with_warnings: messages.scormPreparationCompletedWithWarnings,
      failed: messages.scormPreparationFailed,
      not_applicable: messages.scormPreparationNotApplicable,
    }[status];
    const canPrepare = scormPreparation.canStart || scormPreparation.canRetry;
    const scormProgress = totalModules > 0
      ? Math.round((processedModules / totalModules) * 100)
      : null;
    const warnings = moduleResults.filter((result) => (
      result.outcome === 'source_unavailable' || result.outcome === 'copy_failed'
    ));
    const hasProgress = Number.isFinite(scormProgress);

    return (
      <section className="mt-3" aria-labelledby="scorm-preparation-heading">
        <h4 id="scorm-preparation-heading">{intl.formatMessage(messages.scormPreparationHeader)}</h4>
        {statusMessage && <div role="status" aria-live="polite">{intl.formatMessage(statusMessage)}</div>}
        {hasProgress && (
          <>
            <ProgressBar className="mt-2" now={scormProgress} label={intl.formatMessage(messages.scormPreparationProgress, { progress: scormProgress })} />
            <div className="small mt-1">{intl.formatMessage(messages.scormPreparationProgress, { progress: scormProgress })}</div>
          </>
        )}
        {canPrepare && (
          <Button className="mt-2" onClick={handleScormPreparation} disabled={isScormPreparationLoading}>
            {intl.formatMessage(scormPreparation.canRetry ? messages.scormPreparationRetry : messages.scormPreparationStart)}
          </Button>
        )}
        {warnings.length > 0 && (
          <Alert className="mt-3" variant="warning">
            <p>{intl.formatMessage(messages.scormPreparationWarnings)}</p>
            <ul className="mb-0">
              {warnings.map((warning) => (
                <li key={`${warning.moduleLabel}-${warning.remediation}`}>
                  <strong>{warning.moduleDisplayName}</strong>{warning.remediation && `: ${warning.remediation}`}
                  {warning.editUrl && (
                    <> <a href={`${getConfig().STUDIO_BASE_URL}${warning.editUrl}`}>{intl.formatMessage(messages.scormPreparationEditModule)}</a></>
                  )}
                </li>
              ))}
            </ul>
          </Alert>
        )}
      </section>
    );
  };

  const steps = [
    {
      title: intl.formatMessage(messages.stepperUploadingTitle),
      description: intl.formatMessage(messages.stepperUploadingDescription),
      key: IMPORT_STAGES.UPLOADING,
    }, {
      title: intl.formatMessage(messages.stepperUnpackingTitle),
      description: intl.formatMessage(messages.stepperUnpackingDescription),
      key: IMPORT_STAGES.UNPACKING,
    }, {
      title: intl.formatMessage(messages.stepperVerifyingTitle),
      description: intl.formatMessage(messages.stepperVerifyingDescription),
      key: IMPORT_STAGES.VERIFYING,
    }, {
      title: intl.formatMessage(messages.stepperUpdatingTitle),
      description: intl.formatMessage(messages.stepperUpdatingDescription),
      key: IMPORT_STAGES.UPDATING,
    }, {
      title: successTitle,
      description: intl.formatMessage(messages.stepperSuccessDescription),
      key: IMPORT_STAGES.SUCCESS,
    },
  ];

  return (
    <section>
      <h3 className="mt-4">{intl.formatMessage(messages.stepperHeaderTitle)}</h3>
      <CourseStepper
        courseId={courseId}
        percent={currentStage === IMPORT_STAGES.UPLOADING ? progress : null}
        steps={steps}
        activeKey={currentStage}
        hasError={hasError}
        errorMessage={formattedErrorMessage}
      />
      {currentStage === IMPORT_STAGES.SUCCESS && (
        <>
          <Button className="ml-5.5 mt-n2.5" onClick={handleRedirectCourseOutline}>{intl.formatMessage(messages.viewOutlineButton)}</Button>
          {renderScormPreparation()}
        </>
      )}
    </section>
  );
};

ImportStepper.propTypes = {
  intl: intlShape.isRequired,
  courseId: PropTypes.string.isRequired,
};

export default injectIntl(ImportStepper);
